/**
 * Token de sessão (reCAPTCHA) armazenado em cookie.
 * Validade: 1 hora (max-age=3600). O navegador remove o cookie ao expirar.
 */

import axios from 'axios'
import { API_URL } from '@/config'
import store from '@/store'
import { AUTH_SESSION } from '@/services/enpoints'
import clientId from '@/services/clientId'

const COOKIE_NAME = 'fts_session_token'
const MAX_AGE_SECONDS = 3600 // 1 hora

/**
 * @returns {string|null} Valor do token ou null se não existir/expirado
 */
function getToken () {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`))
  if (!match) return null
  try {
    return decodeURIComponent(match[1].trim())
  } catch {
    return null
  }
}

/**
 * Persiste o token em cookie com expiração de 1 hora.
 * @param {string} token
 */
function setToken (token) {
  if (!token) return
  const secure = typeof location !== 'undefined' && location.protocol === 'https:'
  let cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Strict`
  if (secure) cookie += '; Secure'
  document.cookie = cookie
}

/**
 * Remove o cookie do token de sessão.
 */
function clearToken () {
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Strict`
}

/**
 * Single-flight: garante no máximo uma chamada concorrente a /auth.
 */
let inflight = null

/**
 * Garante que existe um fts_session_token nos cookies.
 * - Se já existir: retorna ele (NÃO chama /auth).
 * - Se não existir: chama /auth UMA vez (single-flight) com CNPJ + token
 *   internos do app, persiste no cookie e retorna o valor já normalizado.
 * @returns {Promise<string|null>}
 */
async function ensureToken (interactionId) {
  const existing = getToken()
  if (existing) return existing
  if (inflight) return inflight

  inflight = fetchFromAuth(interactionId)
    .then(token => {
      if (token) setToken(token)
      return getToken()
    })
    .finally(() => { inflight = null })

  return inflight
}

/**
 * Chama POST /auth enviando CNPJ + token no body. O backend monta o
 * Basic Auth internamente. Usa axios "cru" para não passar pelos
 * interceptors do API e evitar loop.
 * @returns {Promise<string|null>} token já com prefixo "Bearer "
 */
async function fetchFromAuth (interactionId) {
  const cnpj = store.state.auth.cnpj
  const internalToken = store.state.auth.token
  if (!cnpj || !internalToken) return null

  try {
    const response = await axios({
      method: 'POST',
      baseURL: API_URL,
      url: AUTH_SESSION,
      headers: {
        'X-Client-Id': clientId.getClientId(),
        'X-Interaction-Id': interactionId || clientId.generateUuidV4(),
        'X-Request-Id': clientId.generateUuidV4(),
      },
      timeout: 45000,
      data: {
        cnpj: cnpj,
        token: internalToken,
      },
    })
    const raw = response && response.data && response.data.token
    return raw ? `Bearer ${raw}` : null
  } catch {
    return null
  }
}

export default {
  getToken,
  setToken,
  clearToken,
  ensureToken,
}
